/**
 * DatePicker - 날짜 선택 컴포넌트
 *
 * Portal 기반 캘린더 드롭다운으로 날짜를 선택한다.
 * @param minDate / maxDate - 선택 가능한 날짜 범위 제한
 * @param presets - 빠른 선택 프리셋 (오늘, 어제, 지난주 등)
 * @param showFooter - true이면 취소/적용 버튼이 있는 확인 모드
 */
'use client';

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Calendar, ChevronLeft, ChevronRight } from 'lucide-react';
import styles from './DatePicker.module.css';

export interface DatePreset {
  label: string;
  getValue: () => Date | null;
}

export interface DatePickerProps {
  value?: Date | null;
  onChange: (date: Date | null) => void;
  label?: string;
  placeholder?: string;
  error?: string;
  helperText?: string;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  locale?: string;
  className?: string;
  showFooter?: boolean;
  presets?: DatePreset[];
}

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];

const DEFAULT_PRESETS: DatePreset[] = [
  {
    label: '오늘',
    getValue: () => new Date(),
  },
  {
    label: '어제',
    getValue: () => {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      return d;
    },
  },
  {
    label: '지난주',
    getValue: () => {
      const d = new Date();
      d.setDate(d.getDate() - 7);
      return d;
    },
  },
  {
    label: '이번 달',
    getValue: () => new Date(new Date().getFullYear(), new Date().getMonth(), 1),
  },
  {
    label: '전체',
    getValue: () => null,
  },
];

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isDateDisabled(date: Date, minDate?: Date, maxDate?: Date): boolean {
  if (minDate) {
    const min = new Date(minDate.getFullYear(), minDate.getMonth(), minDate.getDate());
    if (date < min) return true;
  }
  if (maxDate) {
    const max = new Date(maxDate.getFullYear(), maxDate.getMonth(), maxDate.getDate());
    if (date > max) return true;
  }
  return false;
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

export default function DatePicker({
  value,
  onChange,
  label,
  placeholder = '날짜 선택',
  error,
  helperText,
  minDate,
  maxDate,
  disabled = false,
  locale = 'ko-KR',
  className = '',
  showFooter = false,
  presets,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() =>
    value ? value.getFullYear() : new Date().getFullYear(),
  );
  const [viewMonth, setViewMonth] = useState(() =>
    value ? value.getMonth() : new Date().getMonth(),
  );
  const [focusedDay, setFocusedDay] = useState<number | null>(null);
  const [dropdownPos, setDropdownPos] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [pendingDate, setPendingDate] = useState<Date | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerBtnRef = useRef<HTMLButtonElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  const hasPresets = Boolean(presets) || Boolean(presets === undefined && showFooter);
  const resolvedPresets = presets || (hasPresets ? DEFAULT_PRESETS : []);

  // Recalculate "today" each time the calendar opens
  const today = useMemo(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recalculate "today" only when the calendar opens, not on every render
  }, [open]);

  const hasError = Boolean(error);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      const dropdownEl = document.querySelector(`.${styles.dropdown}`);
      if (dropdownEl?.contains(target)) return;
      setOpen(false);
      setDropdownPos(null);
      setPendingDate(null);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Sync view to value when opening
  useEffect(() => {
    if (open && value) {
      setViewYear(value.getFullYear());
      setViewMonth(value.getMonth());
      setFocusedDay(value.getDate());
      setPendingDate(value);
    } else if (open) {
      setFocusedDay(null);
      setPendingDate(null);
    }
  }, [open, value]);

  const prevMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 0) {
        setViewYear((y) => y - 1);
        return 11;
      }
      return m - 1;
    });
    setFocusedDay(null);
  }, []);

  const nextMonth = useCallback(() => {
    setViewMonth((m) => {
      if (m === 11) {
        setViewYear((y) => y + 1);
        return 0;
      }
      return m + 1;
    });
    setFocusedDay(null);
  }, []);

  const selectDay = useCallback(
    (day: number) => {
      const date = new Date(viewYear, viewMonth, day);
      if (isDateDisabled(date, minDate, maxDate)) return;
      if (showFooter) {
        setPendingDate(date);
        setFocusedDay(day);
      } else {
        onChange(date);
        setOpen(false);
        setDropdownPos(null);
      }
    },
    [viewYear, viewMonth, minDate, maxDate, onChange, showFooter],
  );

  const handleApply = useCallback(() => {
    if (pendingDate) {
      onChange(pendingDate);
    }
    setOpen(false);
    setDropdownPos(null);
    setPendingDate(null);
  }, [pendingDate, onChange]);

  const handleCancel = useCallback(() => {
    setOpen(false);
    setDropdownPos(null);
    setPendingDate(null);
  }, []);

  const handlePresetClick = useCallback(
    (preset: DatePreset) => {
      const date = preset.getValue();
      if (showFooter) {
        setPendingDate(date);
        if (date) {
          setViewYear(date.getFullYear());
          setViewMonth(date.getMonth());
          setFocusedDay(date.getDate());
        }
      } else {
        onChange(date);
        setOpen(false);
        setDropdownPos(null);
      }
    },
    [showFooter, onChange],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!open) return;

      const daysInMonth = getDaysInMonth(viewYear, viewMonth);
      let current =
        focusedDay ??
        (value && value.getMonth() === viewMonth && value.getFullYear() === viewYear
          ? value.getDate()
          : 1);

      switch (e.key) {
        case 'ArrowRight':
          e.preventDefault();
          setFocusedDay(current < daysInMonth ? current + 1 : 1);
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setFocusedDay(current > 1 ? current - 1 : daysInMonth);
          break;
        case 'ArrowDown':
          e.preventDefault();
          setFocusedDay(current + 7 <= daysInMonth ? current + 7 : current + 7 - daysInMonth);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedDay(current - 7 >= 1 ? current - 7 : daysInMonth + current - 7);
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedDay) selectDay(focusedDay);
          break;
        case 'Escape':
          e.preventDefault();
          setOpen(false);
          setDropdownPos(null);
          setPendingDate(null);
          break;
      }
    },
    [open, focusedDay, viewYear, viewMonth, value, selectDay],
  );

  const displayValue = value
    ? value.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  const monthLabel = `${new Date(viewYear, viewMonth).toLocaleDateString('en-US', { month: 'long' })} ${viewYear}`;

  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  // Get previous month days for leading empty cells
  const prevMonthDays = getDaysInMonth(
    viewMonth === 0 ? viewYear - 1 : viewYear,
    viewMonth === 0 ? 11 : viewMonth - 1,
  );

  const inputId = label ? `datepicker-${label.replace(/\s+/g, '-').toLowerCase()}` : undefined;

  // The "active" date for highlighting is pendingDate (when footer mode) or value
  const activeDate = showFooter ? pendingDate : value;

  const dropdownWidth = resolvedPresets.length > 0 ? 420 : 300;

  return (
    <div
      ref={containerRef}
      className={[styles.container, className].filter(Boolean).join(' ')}
      onKeyDown={handleKeyDown}
    >
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
        </label>
      )}
      <button
        ref={triggerBtnRef}
        type="button"
        id={inputId}
        className={[
          styles.trigger,
          hasError ? styles.triggerError : '',
          disabled ? styles.triggerDisabled : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={() => {
          if (disabled) return;
          setOpen((prev) => {
            if (prev) {
              setDropdownPos(null);
            } else if (triggerBtnRef.current) {
              const rect = triggerBtnRef.current.getBoundingClientRect();
              setDropdownPos({
                top: rect.bottom + 4,
                left: rect.left,
                width: Math.max(rect.width, dropdownWidth),
              });
            }
            return !prev;
          });
        }}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <span className={displayValue ? styles.triggerValue : styles.triggerPlaceholder}>
          {displayValue || placeholder}
        </span>
        <Calendar size={18} className={styles.triggerIcon} />
      </button>

      {hasError && (
        <p className={styles.errorText} role="alert">
          {error}
        </p>
      )}
      {!hasError && helperText && <p className={styles.helperText}>{helperText}</p>}

      {open &&
        dropdownPos &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className={styles.dropdown}
            role="dialog"
            aria-label="날짜 선택"
            style={{
              position: 'fixed',
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
            }}
            onDragStart={(e) => e.preventDefault()}
          >
            <div className={styles.dropdownBody}>
              {resolvedPresets.length > 0 && (
                <div className={styles.presetsSidebar}>
                  <div className={styles.presetsHeader}>
                    <Calendar size={16} />
                    <span>날짜 선택</span>
                  </div>
                  {resolvedPresets.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      className={styles.presetItem}
                      onClick={() => handlePresetClick(preset)}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              )}
              <div className={styles.calendarSection}>
                <div className={styles.header}>
                  <button
                    type="button"
                    className={styles.navButton}
                    onClick={prevMonth}
                    aria-label="이전 달"
                  >
                    <ChevronLeft size={16} />
                  </button>
                  <span className={styles.monthLabel}>{monthLabel}</span>
                  <button
                    type="button"
                    className={styles.navButton}
                    onClick={nextMonth}
                    aria-label="다음 달"
                  >
                    <ChevronRight size={16} />
                  </button>
                </div>

                <div className={styles.dayNames}>
                  {DAY_NAMES.map((d, i) => (
                    <span key={`${d}-${i}`} className={styles.dayName}>
                      {d}
                    </span>
                  ))}
                </div>

                <div ref={gridRef} className={styles.grid} role="grid">
                  {/* Previous month's trailing days */}
                  {Array.from({ length: firstDay }).map((_, i) => {
                    const day = prevMonthDays - firstDay + 1 + i;
                    return (
                      <span key={`prev-${i}`} className={styles.inactiveDay}>
                        {day}
                      </span>
                    );
                  })}

                  {/* Current month days */}
                  {Array.from({ length: daysInMonth }).map((_, i) => {
                    const day = i + 1;
                    const date = new Date(viewYear, viewMonth, day);
                    const isDisabled = isDateDisabled(date, minDate, maxDate);
                    const isSelected = activeDate ? isSameDay(date, activeDate) : false;
                    const isToday = isSameDay(date, today);
                    const isFocused = focusedDay === day;

                    return (
                      <button
                        key={day}
                        type="button"
                        role="gridcell"
                        className={[
                          styles.dayButton,
                          isSelected ? styles.selected : '',
                          isToday && !isSelected ? styles.today : '',
                          isDisabled ? styles.dayDisabled : '',
                          isFocused && !isSelected ? styles.focused : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                        onClick={() => selectDay(day)}
                        disabled={isDisabled}
                        tabIndex={isFocused ? 0 : -1}
                        aria-label={`${viewYear}년 ${viewMonth + 1}월 ${day}일`}
                        aria-selected={isSelected}
                      >
                        {day}
                      </button>
                    );
                  })}

                  {/* Next month's leading days */}
                  {(() => {
                    const totalCells = firstDay + daysInMonth;
                    const remaining = totalCells % 7 === 0 ? 0 : 7 - (totalCells % 7);
                    return Array.from({ length: remaining }).map((_, i) => (
                      <span key={`next-${i}`} className={styles.inactiveDay}>
                        {i + 1}
                      </span>
                    ));
                  })()}
                </div>

                {showFooter && (
                  <div className={styles.footer}>
                    <button type="button" className={styles.cancelButton} onClick={handleCancel}>
                      취소
                    </button>
                    <button type="button" className={styles.applyButton} onClick={handleApply}>
                      적용
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
